/**
 * Berkeley Yeast (Berkeley, California) — bioengineered brewing strains.
 *
 * Berkeley builds each product by adding one trait to a well-known parent
 * strain: thiol release on a London or Vermont ale (TROPICS), the ALDC enzyme
 * on Chico, London or an Andechs lager (FRESH), lactic acid production
 * (GALACTIC), Cascade-like monoterpenes (SUPERBLOOM), stone fruit
 * (SUMMERSTONE), a deleted diastatic gene on a French saison (TAMED) and
 * maltose-negative metabolism for non-alcoholic beer (NA CLASSIC, NA CABANA).
 * The parent strain is named on every product page and is repeated in each
 * description here, because it is what tells a brewer how the yeast will
 * behave before the engineered trait is considered. That these are engineered
 * strains is also stated: it is a fact brewers care about, and Berkeley states
 * it themselves.
 *
 * Attenuation, temperature and flocculation are the figures in the spec table
 * on each product page, fetched 2026-09-03, so every entry is `spec-sheet`.
 * Temperatures are converted from the °F bands Berkeley publishes and rounded
 * to whole degrees. Berkeley prints flocculation as Med / High / Low, which
 * maps onto our vocabulary directly.
 *
 * Alcohol tolerance is not published for any strain, so that field is `null`
 * throughout rather than a guess. STA1 is `false` only where Berkeley
 * publishes a result: the two dry SKUs carry a "Diastaticus: Negative by PCR"
 * line on their maker-hosted specification PDFs, and TAMED Saison's whole
 * selling point is that the diastatic trait of its parent has been removed.
 * The liquid strains are `null` — the same strain in a different package is
 * not a published test result.
 *
 * SUMMERSTONE and TAMED Saison are sold on enquiry rather than through the
 * shop, so their link is Berkeley's own page for the strain rather than a
 * `/products/` page; both were fetched and carry the same spec table.
 * TROPICS BOOST is deliberately absent — it is a plant extract, not a yeast.
 */

import type { Yeast } from '../../schema/yeast';

/** Berkeley Yeast strains. @see Yeast */
export const berkeleyYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // TROPICS — thiol-releasing strains
  // -------------------------------------------------------------------------
  {
    id: 'berkeley-tropics-london',
    lab: 'Berkeley Yeast',
    productCode: 'TROPICS London',
    name: 'TROPICS London',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A London ale parent engineered to release thiols, so the beer arrives with guava, passionfruit and grapefruit already in it before a single hop pellet is weighed. Ferments like the English strain it came from — soft, low attenuation, hazy — which is why it has become the default for New England IPA on this side of the catalog.',
    tags: ['hazy', 'neipa', 'ipa', 'thiol', 'tropical', 'passionfruit', 'english', 'bioengineered'],
    url: 'https://berkeleyyeast.com/products/tropics-london',
    provenance: 'spec-sheet',
  },
  {
    id: 'berkeley-tropics-vermont',
    lab: 'Berkeley Yeast',
    productCode: 'TROPICS Vermont',
    name: 'TROPICS Vermont',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The same thiol-releasing engineering on a Vermont IPA parent, which finishes several points drier than the London version. Pick it when a hazy or clear IPA should stay tropical without the residual sweetness, and pitch it heavier — Berkeley asks for roughly twice the cells of its London sibling.',
    tags: ['hazy', 'neipa', 'ipa', 'thiol', 'tropical', 'vermont', 'bioengineered'],
    url: 'https://berkeleyyeast.com/products/tropics-vermont',
    provenance: 'spec-sheet',
  },
  {
    id: 'berkeley-dry-tropics-london',
    lab: 'Berkeley Yeast',
    productCode: 'Dry TROPICS London',
    name: 'Dry TROPICS London',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'TROPICS London in a 500 g dry sachet: the same London ale parent engineered for thiol release, with the pillowy mouthfeel and the grapefruit-and-passionfruit lift, but no propagation and no cold chain. The specification sheet reports diastaticus negative by PCR.',
    tags: [
      'hazy',
      'neipa',
      'ipa',
      'thiol',
      'tropical',
      'passionfruit',
      'english',
      'bioengineered',
      'dry',
    ],
    url: 'https://berkeleyyeast.com/products/dry-tropics-london',
    dosageGramsPerHl: { min: 50, max: 150 },
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // FRESH — ALDC-expressing strains
  // -------------------------------------------------------------------------
  {
    id: 'berkeley-fresh-chico',
    lab: 'Berkeley Yeast',
    productCode: 'FRESH Chico',
    name: 'FRESH Chico',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Chico derivative engineered to make the ALDC enzyme inside the cell, which converts the diacetyl precursor before it ever becomes butter. Ferments like the American ale workhorse it came from, but shortens or removes the diacetyl rest, so tanks turn over faster.',
    tags: [
      'clean',
      'neutral',
      'american',
      'pale-ale',
      'ipa',
      'aldc',
      'low-diacetyl',
      'bioengineered',
    ],
    url: 'https://berkeleyyeast.com/products/fresh-chico',
    provenance: 'spec-sheet',
  },
  {
    id: 'berkeley-dry-fresh-chico',
    lab: 'Berkeley Yeast',
    productCode: 'Dry FRESH Chico',
    name: 'Dry FRESH Chico',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'The dry sachet of the ALDC-expressing Chico strain — a neutral American house yeast that keeps diacetyl ultra-low without a rest, pitched straight from the pack. The specification sheet reports diastaticus negative by PCR.',
    tags: [
      'clean',
      'neutral',
      'american',
      'pale-ale',
      'ipa',
      'aldc',
      'low-diacetyl',
      'bioengineered',
      'dry',
    ],
    url: 'https://berkeleyyeast.com/products/dry-fresh-chico',
    dosageGramsPerHl: { min: 50, max: 150 },
    provenance: 'spec-sheet',
  },
  {
    id: 'berkeley-fresh-london',
    lab: 'Berkeley Yeast',
    productCode: 'FRESH London',
    name: 'FRESH London',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A London ale parent carrying the same ALDC engineering as FRESH Chico. It leaves the low attenuation, full body and stable haze the English strain is kept for, and takes the buttery note off the table — useful in a hazy beer where a big dry hop would otherwise push diacetyl back up.',
    tags: ['hazy', 'english', 'neipa', 'full-body', 'aldc', 'low-diacetyl', 'bioengineered'],
    url: 'https://berkeleyyeast.com/products/fresh-london',
    provenance: 'spec-sheet',
  },
  {
    id: 'berkeley-fresh-andechs',
    lab: 'Berkeley Yeast',
    productCode: 'FRESH Andechs',
    name: 'FRESH Andechs',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 75 },
    temperatureC: { min: 10, max: 17 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'An Andechs lager strain engineered to express ALDC, so a helles or pilsner can be packaged without the long diacetyl rest a lager usually demands. Low sulfur, crops and repitches well, and covers dark lagers as readily as pale ones.',
    tags: [
      'lager',
      'helles',
      'pilsner',
      'clean',
      'low-sulfur',
      'aldc',
      'low-diacetyl',
      'bioengineered',
    ],
    url: 'https://berkeleyyeast.com/products/fresh-andechs',
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // Flavour series — GALACTIC, SUPERBLOOM, SUMMERSTONE
  // -------------------------------------------------------------------------
  {
    id: 'berkeley-galactic',
    lab: 'Berkeley Yeast',
    productCode: 'GALACTIC',
    name: 'GALACTIC',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Saccharomyces cerevisiae engineered to produce lactic acid during a normal primary fermentation, tuned to land around pH 3.5. It is a single pitch in a clean fermenter — no kettle sour, no Lactobacillus, no separate souring vessel — so it is typed as an ale rather than a wild culture. Fruit added afterwards will drive the acidity further.',
    tags: ['sour', 'lactic', 'kettle-sour-alternative', 'gose', 'berliner-weisse', 'bioengineered'],
    url: 'https://berkeleyyeast.com/products/galactic-chico',
    provenance: 'spec-sheet',
  },
  {
    id: 'berkeley-superbloom',
    lab: 'Berkeley Yeast',
    productCode: 'SUPERBLOOM',
    name: 'SUPERBLOOM',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Saccharomyces cerevisiae engineered to make the terpenes that carry classic Cascade character, so the beer reads of orange blossom, geranium, lime peel and lemon zest without the vegetal edge a heavy dry hop brings. Suits light-bodied, low-bitterness beers where the hop bill is deliberately thin.',
    tags: ['citrus', 'floral', 'cascade', 'pale-ale', 'low-bitterness', 'bioengineered'],
    url: 'https://berkeleyyeast.com/products/superbloom',
    provenance: 'spec-sheet',
  },
  {
    id: 'berkeley-summerstone',
    lab: 'Berkeley Yeast',
    productCode: 'SUMMERSTONE',
    name: 'SUMMERSTONE',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Saccharomyces cerevisiae engineered for peach and apricot character, aimed at the problem that stone fruit flavor fades fast in beer even when the puree bill is enormous. Flocculates hard. Sold on enquiry rather than through the shop.',
    tags: ['stone-fruit', 'peach', 'apricot', 'fruit-beer', 'clearing', 'bioengineered'],
    url: 'https://berkeleyyeast.com/pages/summerstone-strains',
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // TAMED — wild strains with the awkward trait removed
  // -------------------------------------------------------------------------
  {
    id: 'berkeley-tamed-saison',
    lab: 'Berkeley Yeast',
    productCode: 'TAMED Saison',
    name: 'TAMED Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 90 },
    temperatureC: { min: 18, max: 31 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A French saison strain with the diastatic trait engineered out, which is the whole point: the farmhouse character and the enormous temperature envelope stay, and the risk of a super-attenuating escapee working its way through a brewery goes away. Sold on enquiry rather than through the shop.',
    tags: ['saison', 'farmhouse', 'belgian', 'non-diastatic', 'warm-ferment', 'bioengineered'],
    url: 'https://berkeleyyeast.com/pages/tamed-strains',
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // NA — maltose-negative strains for non-alcoholic beer
  // -------------------------------------------------------------------------
  {
    id: 'berkeley-na-classic',
    lab: 'Berkeley Yeast',
    productCode: 'NA CLASSIC',
    name: 'NA CLASSIC',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 10, max: 25 },
    temperatureC: { min: 19, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Saccharomyces cerevisiae engineered so it cannot use maltose or maltotriose, which caps alcohol production near zero while still fermenting enough to build classic craft-beer character. Pitched very heavily — Berkeley specifies a cell count per milliliter rather than per degree Plato — and the finished beer needs pasteurizing.',
    tags: [
      'non-alcoholic',
      'na',
      'maltose-negative',
      'low-attenuation',
      'low-alcohol',
      'bioengineered',
    ],
    url: 'https://berkeleyyeast.com/products/na-classic-bayfair',
    provenance: 'spec-sheet',
  },
  {
    id: 'berkeley-na-cabana',
    lab: 'Berkeley Yeast',
    productCode: 'NA CABANA',
    name: 'NA CABANA',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 10, max: 25 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The maltose-negative non-alcoholic strain with tropical character engineered on top, so a zero-alcohol hazy or clear IPA tastes dry-hopped without the bitterness and burn. Same caveats as NA CLASSIC: a heavy pitch, a short fermentation and pasteurization before packaging.',
    tags: [
      'non-alcoholic',
      'na',
      'ipa',
      'hazy',
      'tropical',
      'maltose-negative',
      'low-attenuation',
      'bioengineered',
    ],
    url: 'https://berkeleyyeast.com/products/na-cabana-bayfair',
    provenance: 'spec-sheet',
  },
];
